'use client';

import { useEffect, useMemo, useRef } from 'react';
import {
  ContentCard,
  getSignalCategoriesForTarget,
  SignalCategoriesContent,
} from '@terra/ui-design-system';
import { useIsMcpServerTarget } from '../../../store/projectStore';
import { useAutomationSettings } from '../automation/useAutomationSettings';
import {
  resolveTargetSignalCategoriesInScope,
  toSignalCategoryTargetType,
  usesResolvedSignalCategoryScope,
} from './target-signal-categories.model';

type TargetSignalCategoriesPanelProps = {
  isReadOnly?: boolean;
};

export function TargetSignalCategoriesPanel({
  isReadOnly,
}: TargetSignalCategoriesPanelProps) {
  const {
    selectedTarget,
    signalCategoriesInScope,
    setSignalCategoriesInScope,
  } = useAutomationSettings();
  const isMcpServerTarget = useIsMcpServerTarget();
  const targetType = toSignalCategoryTargetType({
    targetType: selectedTarget?.targetType,
    isMcpServerTarget,
  });
  const resolvedCategories = useMemo(
    () =>
      resolveTargetSignalCategoriesInScope({
        stored: signalCategoriesInScope,
        targetType,
      }),
    [signalCategoriesInScope, targetType],
  );
  const seededTargetIdRef = useRef<string | null>(null);
  const targetId = selectedTarget?.id;
  const hasStoredCategories = signalCategoriesInScope.length > 0;

  useEffect(() => {
    if (
      isReadOnly ||
      !targetId ||
      hasStoredCategories ||
      !usesResolvedSignalCategoryScope(targetType) ||
      seededTargetIdRef.current === targetId
    ) {
      return;
    }

    seededTargetIdRef.current = targetId;
    void setSignalCategoriesInScope(
      getSignalCategoriesForTarget({ targetType }),
    );
  }, [
    hasStoredCategories,
    isReadOnly,
    setSignalCategoriesInScope,
    targetId,
    targetType,
  ]);

  return (
    <ContentCard minHeight="340px">
      <SignalCategoriesContent
        signalCategoriesInScope={resolvedCategories}
        onSignalCategoriesChange={(next) =>
          void setSignalCategoriesInScope(next)
        }
        targetType={targetType}
        isReadOnly={isReadOnly}
        requireDisableConfirmation
        tableMaxHeight="50vh"
      />
    </ContentCard>
  );
}
